/**
 * ロールとライセンス種別。
 *
 * ロールは `mcp < member < admin` の 1 本の階層。2 軸 (license × role) に
 * しないのは、「MCP ライセンスの管理者」のような意味の無い組み合わせを
 * 表現できなくするため。
 *
 * 判定はここに集約する。API と画面で別々に書くと、片方だけ直したときに
 * 「サイドバーには出ないが API は通る」がすぐ生まれる。
 */
import type { MfaPolicy, UserRole } from './types.js';

/** 弱い順。索引がそのまま強さになる。 */
export const ROLES = ['mcp', 'member', 'admin'] as const;

/** ライセンス種別。シートはこの単位で数える。 */
export type License = 'mcp' | 'full';

export const LICENSES = ['mcp', 'full'] as const;

export function isUserRole(value: string): value is UserRole {
  return (ROLES as readonly string[]).includes(value);
}

/** `role` が `min` 以上の権限を持つか。 */
export function roleAtLeast(role: UserRole, min: UserRole): boolean {
  return ROLES.indexOf(role) >= ROLES.indexOf(min);
}

/**
 * そのロールが消費するライセンス枠。
 * 管理画面を使えるロール (member 以上) はフル枠を消費する。
 */
export function licenseOf(role: UserRole): License {
  return role === 'mcp' ? 'mcp' : 'full';
}

/** 二段階認証を要求する範囲の選択肢。弱い順。 */
export const MFA_POLICIES = ['off', 'admin', 'all'] as const;

export function isMfaPolicy(value: string): value is MfaPolicy {
  return (MFA_POLICIES as readonly string[]).includes(value);
}

/**
 * そのロールが、テナントのポリシーで二段階認証を要求されるか。
 *
 * 階層の判定は `roleAtLeast` に委ねる。ここで自前に書くと
 * 「API と画面で別々」がすぐ生まれる。
 */
export function mfaRequiredForRole(policy: MfaPolicy, role: UserRole): boolean {
  if (policy === 'all') return true;
  if (policy === 'admin') return roleAtLeast(role, 'admin');
  return false;
}
